import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import FloatingBubbles from '../components/FloatingBubbles';
import axios from 'axios';

const PROJECT_TYPES = [
  { value: 'créer', label: 'Créer', icon: '🔧', color: '#2B6CB0', bg: '#EBF4FF' },
  { value: 'analyser', label: 'Analyser', icon: '📊', color: '#276749', bg: '#F0FFF4' },
  { value: 'médiatiser', label: 'Médiatiser', icon: '📡', color: '#9B2C2C', bg: '#FFF5F5' },
  { value: 'jouer', label: 'Jouer', icon: '🎮', color: '#6B46C1', bg: '#FAF5FF' },
];

const COLORS = ['#2B6CB0', '#276749', '#9B2C2C', '#6B46C1', '#C05621', '#2C7A7B', '#B7791F', '#553C9A'];

const EMOJIS = ['🚀', '🔥', '⚡', '🎯', '🏆', '💡', '🛠️', '🎨', '📊', '🌍', '🤝', '🧬', '🎵', '🏎️', '✈️', '🤖', '💻', '🌱', '📱', '🎬'];

export default function Projects() {
  const { user, token, API } = useAuth();
  const [projects, setProjects] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', project_type: 'créer', color: '#2B6CB0', is_private: false, emoji: '🚀' });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const fetchProjects = useCallback(async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/projects`, { headers });
      setProjects(res.data);
    } catch (err) { console.error(err); }
    finally { setFetching(false); }
  }, [API, token]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim()) return;
    setCreating(true); setError('');
    try {
      await axios.post(`${API}/projects`, form, { headers: { Authorization: `Bearer ${token}` } });
      setForm({ title: '', description: '', project_type: 'créer', color: '#2B6CB0', is_private: false, emoji: '🚀' });
      setShowCreate(false);
      fetchProjects();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la création');
    } finally { setCreating(false); }
  };

  const typeInfo = (type) => PROJECT_TYPES.find(t => t.value === type) || PROJECT_TYPES[0];

  const filteredProjects = projects.filter(p => {
    if (filter === 'mine') return user && p.members.some(m => m.user.username === user.username);
    if (filter === 'public') return !p.is_private;
    return true;
  });

  return (
    <>
      <FloatingBubbles />
      <div style={{ position: 'relative', zIndex: 1, minHeight: '100vh' }}>

        {/* Hero */}
        <div style={{ background: 'rgba(235,244,255,0.85)', backdropFilter: 'blur(4px)', padding: '48px 20px 0', textAlign: 'center', borderBottom: '1px solid rgba(190,227,248,0.5)' }}>
          <div className="fade-up" style={{ marginBottom: '12px' }}>
            <span style={{ background: 'rgba(43,108,176,0.1)', color: 'var(--blue)', fontSize: '11px', fontWeight: 600, padding: '5px 16px', borderRadius: '20px', letterSpacing: '1px' }}>PROJETS</span>
          </div>
          <h1 className="fade-up" style={{ fontSize: '36px', fontWeight: 800, color: 'var(--dark)', letterSpacing: '-1.5px', marginBottom: '12px' }}>Construis quelque chose</h1>
          <p className="fade-up" style={{ fontSize: '16px', color: 'var(--text)', maxWidth: '420px', margin: '0 auto 24px', lineHeight: 1.7 }}>
            Lance un projet, trouve des collaborateurs, construis ensemble.
          </p>
          {user && (
            <button onClick={() => setShowCreate(!showCreate)} style={{ background: 'var(--blue)', color: '#fff', border: 'none', padding: '12px 28px', borderRadius: '24px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 12px rgba(43,108,176,0.3)', transition: 'all .2s', marginBottom: '24px' }}
              onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
            >
              {showCreate ? '✕ Annuler' : '+ Créer un projet'}
            </button>
          )}
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            {[
              { key: 'all', label: 'Tous' },
              { key: 'public', label: '🌍 Publics' },
              ...(user ? [{ key: 'mine', label: '👤 Mes projets' }] : []),
            ].map(f => (
              <button key={f.key} onClick={() => setFilter(f.key)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '12px 20px', fontSize: '13px', fontWeight: 600, color: filter === f.key ? 'var(--blue)' : 'var(--muted)', borderBottom: `2px solid ${filter === f.key ? 'var(--blue)' : 'transparent'}`, transition: 'all .2s' }}>
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ maxWidth: '900px', margin: '0 auto', padding: '32px 20px 48px' }}>

          {/* Create form */}
          {showCreate && (
            <div className="fade-up" style={{ background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(12px)', borderRadius: '20px', padding: '28px', border: '1px solid var(--blue-border)', boxShadow: 'var(--shadow)', marginBottom: '32px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: 700, color: 'var(--dark)', marginBottom: '20px' }}>Nouveau projet</h2>
              {error && <div style={{ background: '#FFF5F5', color: 'var(--red)', padding: '10px 14px', borderRadius: '10px', marginBottom: '16px', fontSize: '13px', border: '1px solid #FED7D7' }}>{error}</div>}

              <form onSubmit={handleCreate}>
                {/* Emoji + Title */}
                <div style={{ display: 'flex', gap: '12px', marginBottom: '14px', alignItems: 'flex-end' }}>
                  <div style={{ position: 'relative' }}>
                    <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', display: 'block', marginBottom: '6px' }}>Emoji</label>
                    <button type="button" onClick={() => setShowEmojiPicker(!showEmojiPicker)} style={{ width: '56px', height: '48px', background: 'var(--blue-bg)', border: '1.5px solid var(--blue-border)', borderRadius: '12px', fontSize: '24px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {form.emoji}
                    </button>
                    {showEmojiPicker && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, background: '#fff', border: '1px solid var(--border)', borderRadius: '12px', padding: '10px', boxShadow: 'var(--shadow-hover)', zIndex: 20, display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '4px', marginTop: '4px' }}>
                        {EMOJIS.map(e => (
                          <button key={e} type="button" onClick={() => { setForm({ ...form, emoji: e }); setShowEmojiPicker(false); }} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', padding: '6px', borderRadius: '8px', transition: 'background .2s' }}
                            onMouseEnter={e2 => e2.currentTarget.style.background = 'var(--blue-bg)'}
                            onMouseLeave={e2 => e2.currentTarget.style.background = 'none'}
                          >{e}</button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', display: 'block', marginBottom: '6px' }}>Titre</label>
                    <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Ex: Analyse F1 2025" required
                      style={{ width: '100%', padding: '12px 16px', border: '1.5px solid var(--blue-border)', borderRadius: '12px', fontSize: '14px', outline: 'none', background: '#FAFCFF' }}
                      onFocus={e => e.target.style.borderColor = 'var(--blue)'} onBlur={e => e.target.style.borderColor = 'var(--blue-border)'}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '14px' }}>
                  <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', display: 'block', marginBottom: '6px' }}>Description</label>
                  <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Décris ton projet..." rows={3} required
                    style={{ width: '100%', padding: '12px 16px', border: '1.5px solid var(--blue-border)', borderRadius: '12px', fontSize: '14px', outline: 'none', resize: 'none', fontFamily: 'inherit', lineHeight: 1.6, background: '#FAFCFF' }}
                    onFocus={e => e.target.style.borderColor = 'var(--blue)'} onBlur={e => e.target.style.borderColor = 'var(--blue-border)'}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
                  <div>
                    <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', display: 'block', marginBottom: '8px' }}>Type</label>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {PROJECT_TYPES.map(t => (
                        <button key={t.value} type="button" onClick={() => setForm({ ...form, project_type: t.value })} style={{ background: form.project_type === t.value ? t.bg : 'transparent', color: form.project_type === t.value ? t.color : 'var(--muted)', border: `1.5px solid ${form.project_type === t.value ? t.color : 'var(--border)'}`, padding: '6px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', transition: 'all .2s' }}>
                          {t.icon} {t.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', display: 'block', marginBottom: '8px' }}>Visibilité</label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button type="button" onClick={() => setForm({ ...form, is_private: false })} style={{ flex: 1, background: !form.is_private ? '#F0FFF4' : 'transparent', color: !form.is_private ? '#276749' : 'var(--muted)', border: `1.5px solid ${!form.is_private ? '#68D391' : 'var(--border)'}`, padding: '10px', borderRadius: '10px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>🌍 Public</button>
                      <button type="button" onClick={() => setForm({ ...form, is_private: true })} style={{ flex: 1, background: form.is_private ? '#FFF5F5' : 'transparent', color: form.is_private ? '#9B2C2C' : 'var(--muted)', border: `1.5px solid ${form.is_private ? '#FC8181' : 'var(--border)'}`, padding: '10px', borderRadius: '10px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>🔒 Privé</button>
                    </div>
                  </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', display: 'block', marginBottom: '8px' }}>Couleur</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {COLORS.map(c => (
                      <button key={c} type="button" onClick={() => setForm({ ...form, color: c })} style={{ width: '28px', height: '28px', borderRadius: '50%', background: c, border: `3px solid ${form.color === c ? '#fff' : c}`, outline: form.color === c ? `2px solid ${c}` : 'none', cursor: 'pointer', transition: 'all .2s' }} />
                    ))}
                  </div>
                </div>

                <button type="submit" disabled={creating} style={{ background: 'var(--blue)', color: '#fff', border: 'none', padding: '12px 28px', borderRadius: '24px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 12px rgba(43,108,176,0.25)' }}>
                  {creating ? 'Création...' : 'Créer le projet →'}
                </button>
              </form>
            </div>
          )}

          {/* Projects grid */}
          {fetching ? (
            <div style={{ textAlign: 'center', padding: '60px', color: 'var(--muted)' }}>Chargement...</div>
          ) : filteredProjects.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', background: 'rgba(255,255,255,0.8)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🚀</div>
              <p style={{ fontSize: '16px', fontWeight: 600, color: 'var(--dark)', marginBottom: '8px' }}>Aucun projet ici</p>
              <p style={{ fontSize: '14px', color: 'var(--muted)' }}>Sois le premier à en créer un !</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
              {filteredProjects.map((project, i) => {
                const tInfo = typeInfo(project.project_type);
                return (
                  <Link key={project.id} to={`/projects/${project.id}`} style={{ textDecoration: 'none' }}>
                    <div style={{ background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(12px)', borderRadius: '20px', overflow: 'hidden', border: '1px solid var(--border)', boxShadow: 'var(--shadow)', transition: 'all .3s ease', animation: `fadeUp 0.5s ease ${i * 0.06}s both` }}
                      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = 'var(--shadow-hover)'; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'var(--shadow)'; }}
                    >
                      {/* Cover image or color banner */}
                      {project.cover_image ? (
                        <div style={{ height: '100px', overflow: 'hidden', position: 'relative' }}>
                          <img src={`https://social-agora.com${project.cover_image}`} alt="cover" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          <div style={{ position: 'absolute', bottom: '8px', left: '12px', fontSize: '28px' }}>{project.emoji || '🚀'}</div>
                        </div>
                      ) : (
                        <div style={{ height: '80px', background: project.color, opacity: 0.85, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '32px' }}>
                          {project.emoji || '🚀'}
                        </div>
                      )}

                      <div style={{ padding: '16px 18px 18px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                          <span style={{ background: tInfo.bg, color: tInfo.color, fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px' }}>{tInfo.icon} {tInfo.label}</span>
                          <span style={{ marginLeft: 'auto', fontSize: '12px' }}>{project.is_private ? '🔒' : '🌍'}</span>
                        </div>
                        <h3 style={{ fontSize: '16px', fontWeight: 700, color: 'var(--dark)', marginBottom: '6px', letterSpacing: '-0.3px' }}>{project.title}</h3>
                        <p style={{ fontSize: '13px', color: 'var(--text)', lineHeight: 1.6, marginBottom: '12px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          {project.description}
                        </p>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: '12px', color: 'var(--muted)' }}>{project.owner.username}</span>
                          <span style={{ fontSize: '12px', color: 'var(--blue)', fontWeight: 600 }}>{project.members.length} membre{project.members.length > 1 ? 's' : ''}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
